import { z } from "zod/v4";
import { messageReactionSchema, roomChatMessageSchema } from "../schemas/chat-message";
import {
	chatRoomInviteSchema,
	chatRoomSchema,
	roomInviteeSchema,
	roomMemberSchema,
} from "../schemas/chat-room";
import { fetchApi, fetchVoid } from "./internal";

export const chatRooms = {
	create: (clubId: string, name: string) =>
		fetchApi(`/clubs/${clubId}/chat/rooms`, chatRoomSchema, {
			body: JSON.stringify({ name }),
			method: "POST",
		}),
	delete: (clubId: string, roomId: string) =>
		fetchVoid(`/clubs/${clubId}/chat/rooms/${roomId}`, { method: "DELETE" }),
	invite: (clubId: string, roomId: string, userIds: string[]) =>
		fetchVoid(`/clubs/${clubId}/chat/rooms/${roomId}/invites`, {
			body: JSON.stringify({ user_ids: userIds }),
			method: "POST",
		}),
	leave: (clubId: string, roomId: string) =>
		fetchVoid(`/clubs/${clubId}/chat/rooms/${roomId}/members`, {
			method: "DELETE",
		}),
	list: (clubId: string) => fetchApi(`/clubs/${clubId}/chat/rooms`, z.array(chatRoomSchema)),
	members: (clubId: string, roomId: string) =>
		fetchApi(`/clubs/${clubId}/chat/rooms/${roomId}/members`, z.array(roomMemberSchema)),
	rename: (clubId: string, roomId: string, name: string) =>
		fetchApi(`/clubs/${clubId}/chat/rooms/${roomId}`, chatRoomSchema, {
			body: JSON.stringify({ name }),
			method: "PATCH",
		}),
};

export const chatInvites = {
	accept: (clubId: string, inviteId: string) =>
		fetchVoid(`/clubs/${clubId}/chat/invites/${inviteId}/accept`, { method: "POST" }),
	decline: (clubId: string, inviteId: string) =>
		fetchVoid(`/clubs/${clubId}/chat/invites/${inviteId}/decline`, { method: "POST" }),
	forRoom: (clubId: string, roomId: string) =>
		fetchApi(`/clubs/${clubId}/chat/rooms/${roomId}/invites`, z.array(chatRoomInviteSchema)),
	mine: (clubId: string) =>
		fetchApi(`/clubs/${clubId}/chat/invites`, z.array(chatRoomInviteSchema)),
	search: (clubId: string, roomId: string, query: string) =>
		fetchApi(
			`/clubs/${clubId}/chat/rooms/${roomId}/invites/search?q=${encodeURIComponent(query)}`,
			z.array(roomInviteeSchema),
		),
};

export const chatMessages = {
	clearReaction: (clubId: string, roomId: string, messageId: string) =>
		fetchVoid(`/clubs/${clubId}/chat/rooms/${roomId}/messages/${messageId}/reaction`, {
			method: "DELETE",
		}),
	edit: (clubId: string, roomId: string, messageId: string, content: string) =>
		fetchApi(`/clubs/${clubId}/chat/rooms/${roomId}/messages/${messageId}`, roomChatMessageSchema, {
			body: JSON.stringify({ content }),
			method: "PUT",
		}),
	list: (clubId: string, roomId: string, params?: { limit?: number; before?: string }) => {
		const searchParams = new URLSearchParams();
		if (params?.limit) searchParams.set("limit", String(params.limit));
		if (params?.before) searchParams.set("before", params.before);
		const qs = searchParams.toString();
		return fetchApi(
			`/clubs/${clubId}/chat/rooms/${roomId}/messages${qs ? `?${qs}` : ""}`,
			z.array(roomChatMessageSchema),
		);
	},
	markRead: (clubId: string, roomId: string, messageId: string) =>
		fetchVoid(`/clubs/${clubId}/chat/rooms/${roomId}/receipts`, {
			body: JSON.stringify({ last_read_message_id: messageId }),
			method: "PUT",
		}),
	react: (clubId: string, roomId: string, messageId: string, emoji: string) =>
		fetchApi(
			`/clubs/${clubId}/chat/rooms/${roomId}/messages/${messageId}/reaction`,
			z.array(messageReactionSchema),
			{
				body: JSON.stringify({ emoji }),
				method: "PUT",
			},
		),
	send: (clubId: string, roomId: string, message: { content: string; replyToMessageId?: string }) =>
		fetchApi(`/clubs/${clubId}/chat/rooms/${roomId}/messages`, roomChatMessageSchema, {
			body: JSON.stringify({
				content: message.content,
				reply_to_message_id: message.replyToMessageId,
			}),
			method: "POST",
		}),
};
